import { useEffect, useMemo, useState, useRef } from "react";
import styled from "styled-components";
import { Spin } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import type { CollectionNode, CollectionUpsertPayload } from "src/types/collection/collectionTypes";
import { useCollectionStore } from "src/store/collection/collectionStore";
import CollectionParentPickerModal from "src/components/collection/CollectionParentPickerModal";
import CustomButton from "src/components/common/CustomButton";
import { uploadImageApi, deleteFileApi } from "src/api/storage/storageApi";
import { snackbarStore } from "src/store/snackbarStore";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  initial?: CollectionNode | null;
  onClose: () => void;
};

const emptyPayload: CollectionUpsertPayload = {
  parentCollectionId: 0,
  displayEndDate: "",
  collectionName: "",
  collectionCode: "",
  displayOrder: 0,
  displayStartDate: "",
  isActive: true,
  isFeatured: false,
  collectionContent: "",
  collectionDescription: "",
  thumbnailFileGrpId: null,
};

function toLocalDateTimeValue(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function localDateTimeToIso(v: string) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

export default function CollectionUpsertModal({ open, mode, initial, onClose }: Props) {
  /** ===== store ===== */
  const createCollection = useCollectionStore((s) => s.createCollection);
  const updateCollection = useCollectionStore((s) => s.updateCollection);
  const loading = useCollectionStore((s) => s.loading);

  /** ===== state ===== */
  const [payload, setPayload] = useState<CollectionUpsertPayload>(emptyPayload);
  const [thumbnailFileGrpId, setThumbnailFileGrpId] = useState<number | undefined>(undefined);
  const [thumbnailFileId, setThumbnailFileId] = useState<number | undefined>(undefined);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>("");
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailDeleted, setThumbnailDeleted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");

  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<CollectionNode | null>(null);

  // 자동 업로드 및 삭제 핸들러
  const handleThumbnailSelect = async (file: File) => {
    const url = URL.createObjectURL(file);
    setThumbnailPreview(url);

    setThumbnailUploading(true);
    try {
      const res = await uploadImageApi("collections/thumbnails", [file], undefined, true);
      if (res.statusCode === 1 && res.data) {
        setThumbnailFileGrpId(res.data.fileGrpId);
        setThumbnailFileId(res.data.files[0]?.fileId);
        setThumbnailDeleted(false);
        snackbarStore.getState().addSnackbar({
          message: "이미지 업로드 완료",
          severity: "success",
        });
      } else {
        URL.revokeObjectURL(url);
        setThumbnailPreview("");
        snackbarStore.getState().addSnackbar({
          message: res.message || "이미지 업로드 실패",
          severity: "error",
        });
      }
    } catch (e: any) {
      URL.revokeObjectURL(url);
      setThumbnailPreview("");
      snackbarStore.getState().addSnackbar({
        message: e?.message || "이미지 업로드 중 오류 발생",
        severity: "error",
      });
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleThumbnailDelete = async () => {
    try {
      // 새로 업로드한 파일 삭제
      if (thumbnailFileId) {
        await deleteFileApi(thumbnailFileId);
        snackbarStore.getState().addSnackbar({
          message: "이미지가 삭제되었습니다",
          severity: "success",
        });
      }
      // 기존 파일 삭제
      else if (initial?.thumbnailFiles?.[0]?.fileId) {
        await deleteFileApi(initial.thumbnailFiles[0].fileId);
        snackbarStore.getState().addSnackbar({
          message: "이미지가 삭제되었습니다",
          severity: "success",
        });
      }
    } catch (e: any) {
      snackbarStore.getState().addSnackbar({
        message: e?.message || "이미지 삭제 중 오류",
        severity: "error",
      });
      return;
    }

    setThumbnailFileGrpId(undefined);
    setThumbnailFileId(undefined);
    setThumbnailPreview("");
    setThumbnailDeleted(true);
  };

  // 현재 표시할 이미지
  const currentImageSrc = useMemo(() => {
    if (thumbnailDeleted) return "";
    if (thumbnailPreview) return thumbnailPreview;
    return initial?.thumbnailFiles?.[0]?.fileUrl || "";
  }, [thumbnailDeleted, thumbnailPreview, initial]);

  const parentLabel = useMemo(() => {
    if (payload.parentCollectionId === 0) return "루트(부모 없음)";
    if (selectedParent)
      return `${selectedParent.collectionName} (ID: ${selectedParent.collectionId})`;
    return `선택됨 (ID: ${payload.parentCollectionId})`;
  }, [payload.parentCollectionId, selectedParent]);

  /** edit/create 초기화 */
  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initial) {
      setPayload({
        parentCollectionId: initial.parentCollectionId,
        displayStartDate: initial.displayStartDate || "",
        displayEndDate: initial.displayEndDate || "",
        collectionName: initial.collectionName || "",
        collectionCode: initial.collectionCode || "",
        displayOrder: initial.displayOrder || 0,
        isActive: !!initial.isActive,
        isFeatured: !!initial.isFeatured,
        collectionContent: initial.collectionContent || "",
        collectionDescription: initial.collectionDescription || "",
        thumbnailFileGrpId: initial.thumbnailFileGrpId ?? null,
      });

      setSelectedParent(null);
      setStartLocal(toLocalDateTimeValue(initial.displayStartDate));
      setEndLocal(toLocalDateTimeValue(initial.displayEndDate));
      setThumbnailFileGrpId(initial.thumbnailFileGrpId ?? undefined);
      setThumbnailFileId(initial.thumbnailFiles?.[0]?.fileId);
      setThumbnailPreview("");
      setThumbnailDeleted(false);
      return;
    }

    if (mode === "create") {
      setPayload(emptyPayload);
      setSelectedParent(null);
      setStartLocal("");
      setEndLocal("");
      setThumbnailFileGrpId(undefined);
      setThumbnailFileId(undefined);
      setThumbnailPreview("");
      setThumbnailDeleted(false);
    }
  }, [open, mode, initial]);

  /** local datetime → payload ISO 반영 (open 아닐 땐 반영 X) */
  useEffect(() => {
    if (!open) return;
    setPayload((p) => ({ ...p, displayStartDate: localDateTimeToIso(startLocal) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLocal, open]);

  useEffect(() => {
    if (!open) return;
    setPayload((p) => ({ ...p, displayEndDate: localDateTimeToIso(endLocal) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endLocal, open]);

  const onChange = <K extends keyof CollectionUpsertPayload>(
    key: K,
    value: CollectionUpsertPayload[K]
  ) => {
    setPayload((p) => ({ ...p, [key]: value }));
  };

  const validate = () => {
    if (!payload.collectionName.trim()) return "컬렉션명을 입력해주세요.";
    if (mode === "create" && !payload.collectionCode?.trim()) return "컬렉션 코드를 입력해주세요.";
    if (payload.displayOrder < 0) return "진열순은 0 이상이어야 해요.";
    if (thumbnailUploading) return "이미지 업로드 중입니다. 잠시만 기다려주세요.";
    // if (mode === "create" && !thumbnailFileGrpId)
    //   return "썸네일 이미지를 선택해주세요.";
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) return alert(err);

    const submitPayload: CollectionUpsertPayload = {
      ...payload,
      thumbnailFileGrpId: thumbnailFileGrpId ?? null,
    };

    if (mode === "create") {
      await createCollection({ payload: submitPayload });
      onClose();
      return;
    }

    if (mode === "edit") {
      if (!initial) return;
      await updateCollection({ id: initial.collectionId, payload: submitPayload });
      onClose();
    }
  };

  /** ✅ 여기서 UI만 막는다 (hooks는 이미 다 호출됨) */
  if (!open) return null;

  return (
    <>
      <Dim onMouseDown={onClose}>
        <Modal onMouseDown={(e) => e.stopPropagation()}>
          <Top>
            <ModalTitle>{mode === "create" ? "컬렉션 생성" : "컬렉션 수정"}</ModalTitle>
            <XBtn type="button" onClick={onClose}>
              ✕
            </XBtn>
          </Top>

          <Body>
            <Grid>
              {/* 부모 선택 */}
              <Field>
                <Label>부모 컬렉션</Label>
                <ParentRow>
                  <ParentBox title={parentLabel}>{parentLabel}</ParentBox>
                  <CustomButton onClick={() => setParentPickerOpen(true)} disabled={loading}>
                    선택
                  </CustomButton>
                  <CustomButton
                    onClick={() => {
                      setSelectedParent(null);
                      onChange("parentCollectionId", 0);
                    }}
                    disabled={loading}
                  >
                    루트
                  </CustomButton>
                </ParentRow>
                <Help>부모를 선택하면 해당 컬렉션의 하위로 생성됩니다.</Help>
              </Field>

              <Field>
                <Label>진열순</Label>
                <Input
                  type="number"
                  value={payload.displayOrder}
                  onChange={(e) => onChange("displayOrder", Number(e.target.value))}
                />
              </Field>

              <Field>
                <Label>컬렉션 코드</Label>
                <Input
                  value={payload.collectionCode}
                  onChange={(e) => onChange("collectionCode", e.target.value)}
                  placeholder="COLLECTION-001"
                  disabled={mode === "edit"}
                />
              </Field>

              <Field>
                <Label>컬렉션명</Label>
                <Input
                  value={payload.collectionName}
                  onChange={(e) => onChange("collectionName", e.target.value)}
                  placeholder="2025 봄 신상품"
                />
              </Field>

              <Field>
                <Label>노출 시작</Label>
                <Input
                  type="datetime-local"
                  value={startLocal}
                  onChange={(e) => setStartLocal(e.target.value)}
                />
              </Field>

              <Field>
                <Label>노출 종료</Label>
                <Input
                  type="datetime-local"
                  value={endLocal}
                  onChange={(e) => setEndLocal(e.target.value)}
                />
              </Field>

              <FieldRow>
                <Check>
                  <input
                    type="checkbox"
                    checked={payload.isActive}
                    onChange={(e) => onChange("isActive", e.target.checked)}
                  />
                  <span>활성</span>
                </Check>
                <Check>
                  <input
                    type="checkbox"
                    checked={payload.isFeatured}
                    onChange={(e) => onChange("isFeatured", e.target.checked)}
                  />
                  <span>대표(Featured)</span>
                </Check>
              </FieldRow>

              <FieldFull>
                <Label>설명</Label>
                <Textarea
                  value={payload.collectionDescription}
                  onChange={(e) => onChange("collectionDescription", e.target.value)}
                  rows={3}
                />
              </FieldFull>

              <FieldFull>
                <Label>콘텐츠(HTML)</Label>
                <Textarea
                  value={payload.collectionContent}
                  onChange={(e) => onChange("collectionContent", e.target.value)}
                  rows={6}
                  placeholder="<p>상세 설명</p>"
                />
              </FieldFull>

              <FieldFull>
                <Label>썸네일 이미지</Label>
                {currentImageSrc ? (
                  <ImageContainer
                    onClick={() => !thumbnailUploading && fileInputRef.current?.click()}
                  >
                    <PreviewImage src={currentImageSrc} alt="thumbnail preview" />
                    {thumbnailUploading && (
                      <LoadingOverlay>
                        <Spin />
                      </LoadingOverlay>
                    )}
                    <HiddenInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleThumbnailSelect(file);
                        e.currentTarget.value = "";
                      }}
                    />
                    <DeleteButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleThumbnailDelete();
                      }}
                    >
                      <DeleteOutlined />
                    </DeleteButton>
                  </ImageContainer>
                ) : (
                  <UploadLabel>
                    <HiddenInput
                      type="file"
                      accept="image/*"
                      disabled={thumbnailUploading || loading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleThumbnailSelect(file);
                      }}
                    />
                    {thumbnailUploading ? (
                      <Spin />
                    ) : (
                      <PlusOutlined style={{ fontSize: 24, color: "#999" }} />
                    )}
                  </UploadLabel>
                )}
              </FieldFull>
            </Grid>
          </Body>

          <Bottom>
            <GhostBtn type="button" onClick={onClose} disabled={loading}>
              취소
            </GhostBtn>
            <PrimaryBtn type="button" onClick={onSubmit} disabled={loading}>
              {loading ? "처리중..." : mode === "create" ? "생성" : "수정"}
            </PrimaryBtn>
          </Bottom>
        </Modal>
      </Dim>

      <CollectionParentPickerModal
        open={parentPickerOpen}
        onClose={() => setParentPickerOpen(false)}
        disabledIds={[...(initial?.collectionId ? [initial.collectionId] : [])]}
        onSelect={(node) => {
          setSelectedParent(node);
          onChange("parentCollectionId", node.collectionId);
        }}
      />
    </>
  );
}

/* ===== styles ===== */

const Dim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Modal = styled.div`
  width: 920px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
`;

const Top = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
`;

const XBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #eee;
  background: #fff;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldRow = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 18px;
  align-items: center;
`;

const FieldFull = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.div`
  font-size: 12px;
  color: #666;
`;

const Help = styled.div`
  font-size: 12px;
  color: #999;
`;

const Input = styled.input`
  height: 38px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
`;

const Textarea = styled.textarea`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  resize: vertical;
`;

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background-color: rgba(255, 255, 255, 0.9);
  color: #ff4d4f;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: #ff4d4f;
    color: #fff;
  }
`;

const UploadLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #1890ff;
  }
`;

const Bottom = styled.div`
  padding: 14px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Btn = styled.button`
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: #fff;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const GhostBtn = styled(Btn)``;

const PrimaryBtn = styled(Btn)`
  background: #111;
  color: #fff;
  border: 0;
`;

const ParentRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ParentBox = styled.div`
  flex: 1;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
